import { COUNTRY_CODE, HarvestUnitValue } from "consts";

import { DairyCowType, DairyFeedType, ENUM_DAIRY_FEED_ENERGY_UNIT, NullableRangeObject, PigletFeedType, PorkFeedType } from "models";

export interface EmissionsFactorRefrigerant {
  id: string;
  name: string;
  emissionFactorKg: number;
  enabled: boolean;
}

export const FARM_FOOTPRINTING_DAIRY_MILK_PRODUCTION = {
  PRODUCED: "produced",
  DELIVERED: "delivered",
} as const;

export const FARM_FOOTPRINTING_DAIRY_MOORLAND = {
  YES: "yes",
  NO: "no",
} as const;

export const FARM_FOOTPRINTING_BEEF_MOORLAND = {
  YES: "yes",
  NO: "no",
} as const;

export const FARM_FOOTPRINTING_DAIRY_MANURE_ADDITIVE_ACTIVE = {
  YES: "yes",
  NO: "no",
} as const;

export const FARM_FOOTPRINTING_DAIRY_KNOWN_COW_WEIGHT = {
  YES: "yes",
  NO: "no",
} as const;

export const FARM_FOOTPRINTING_DAIRY_COWS_IN_PASTURE = {
  YES: "yes",
  NO: "no",
} as const;

export const DAIRY_GRASSLAND_FERTILISER_ACTIVE = {
  YES: "yes",
  NO: "no",
} as const;

export type ENUM_DAIRY_GRASSLAND_FERTILISER_ACTIVE = (typeof DAIRY_GRASSLAND_FERTILISER_ACTIVE)[keyof typeof DAIRY_GRASSLAND_FERTILISER_ACTIVE];

export const DAIRY_REFRIGERANTS_ACTIVE = {
  YES: "yes",
  NO: "no",
} as const;

export type ENUM_DAIRY_REFRIGERANTS_ACTIVE = (typeof DAIRY_REFRIGERANTS_ACTIVE)[keyof typeof DAIRY_REFRIGERANTS_ACTIVE];

export const FARM_FOOTPRINTING_BEEF_COWS_IN_PASTURE = {
  YES: "yes",
  NO: "no",
} as const;

export type ENUM_FARM_FOOTPRINTING_BEEF_COWS_IN_PASTURE = (typeof FARM_FOOTPRINTING_BEEF_COWS_IN_PASTURE)[keyof typeof FARM_FOOTPRINTING_BEEF_COWS_IN_PASTURE];

export const BEEF_GRASSLAND_FERTILISER_ACTIVE = {
  YES: "yes",
  NO: "no",
} as const;

export type ENUM_BEEF_GRASSLAND_FERTILISER_ACTIVE = (typeof BEEF_GRASSLAND_FERTILISER_ACTIVE)[keyof typeof BEEF_GRASSLAND_FERTILISER_ACTIVE];

export const FARM_FOOTPRINTING_BEEF_FEED_ADDITIVE_ACTIVE = {
  YES: "yes",
  NO: "no",
} as const;

export type ENUM_FARM_FOOTPRINTING_BEEF_FEED_ADDITIVE_ACTIVE =
  (typeof FARM_FOOTPRINTING_BEEF_FEED_ADDITIVE_ACTIVE)[keyof typeof FARM_FOOTPRINTING_BEEF_FEED_ADDITIVE_ACTIVE];

export const FARM_FOOTPRINTING_BEEF_MANURE_ADDITIVE_ACTIVE = {
  YES: "yes",
  NO: "no",
} as const;

export type ENUM_FARM_FOOTPRINTING_BEEF_MANURE_ADDITIVE_ACTIVE =
  (typeof FARM_FOOTPRINTING_BEEF_MANURE_ADDITIVE_ACTIVE)[keyof typeof FARM_FOOTPRINTING_BEEF_MANURE_ADDITIVE_ACTIVE];

export const FARM_FOOTPRINTING_DAIRY_FEED_ADDITIVES = {
  YES: "yes",
  NO: "no",
} as const;

export const FARM_FOOTPRINTING_DAIRY_COW_BODY_CONDITION = {
  THIN: "thin",
  MODERATE: "moderate",
  FAT: "fat",
} as const;

export const FARM_FOOTPRINTING_DAIRY_CALVES_FEED = {
  MAT: "mat",
  WHOLE_MILK: "whole_milk",
} as const;

export const FARM_FOOTPRINTING_DAIRY_FEED_PRODUCTION_CROP_USAGE = {
  YES: "yes",
  NO: "no",
} as const;

export const FARM_FOOTPRINTING_OVERALL_SOIL_TYPE = {
  CLAY: "clay",
  SILT: "silt",
  SAND: "sand",
} as const;

export const FARM_FOOTPRINTING_HAS_LAND_CHANGED = {
  YES: "yesLUC",
  NO: "noLUC",
  UNKNOWN: "unknownLUC",
} as const;

export const FARM_FOOTPRINTING_AVERAGE_SOIL_SATURATION = {
  UNKNOWN: "unknown",
  LOW: "low",
  MEDIUM: "medium",
  HIGH: "high",
} as const;

export const CFP_CROP_RESIDUE_MANAGEMENT_PRACTICE = {
  LEFT_ON_FIELD: "left_on_field",
  EXPORTING: "exporting",
  BURNING: "burning",
  LEFT_IN_HEAPS_OR_PITS: "left_in_heaps_or_pits",
  COMPOSTING_FORCED_AERATION: "composting_forced_aeration",
  COMPOSTING_NON_FORCED_AERATION: "composting_non_forced_aeration",
} as const;

export type ENUM_CFP_CROP_RESIDUE_MANAGEMENT_PRACTICE = (typeof CFP_CROP_RESIDUE_MANAGEMENT_PRACTICE)[keyof typeof CFP_CROP_RESIDUE_MANAGEMENT_PRACTICE];

export const CFP_CROP_IRRIGATION_APPLICATION_METHOD = {
  CENTRE_PIVOT: "centre_pivot",
  DRIP: "drip",
  FLOODING: "flooding",
  RAIN_GUN: "rain_gun",
} as const;

export type ENUM_CFP_CROP_IRRIGATION_APPLICATION_METHOD = (typeof CFP_CROP_IRRIGATION_APPLICATION_METHOD)[keyof typeof CFP_CROP_IRRIGATION_APPLICATION_METHOD];

export const CFP_CROP_IRRIGATION_WATER_SOURCE = {
  BOREHOLE: "borehole",
  MAINS: "mains",
  LAKE: "lake",
  POND: "pond",
  RESERVOIR: "reservoir",
  RIVER: "river",
} as const;

export type ENUM_CFP_CROP_IRRIGATION_WATER_SOURCE = (typeof CFP_CROP_IRRIGATION_WATER_SOURCE)[keyof typeof CFP_CROP_IRRIGATION_WATER_SOURCE];

export const CFP_CROP_IRRIGATION_PUMP_POWER_SOURCE = {
  DIESEL: "diesel",
  BLENDED_DIESEL: "blended_diesel",
  ELECTRIC: "electric",
  GRAVITY: "gravity",
} as const;

export type ENUM_CFP_CROP_IRRIGATION_PUMP_POWER_SOURCE = (typeof CFP_CROP_IRRIGATION_PUMP_POWER_SOURCE)[keyof typeof CFP_CROP_IRRIGATION_PUMP_POWER_SOURCE];

export const FARM_FOOTPRINTING_DRYING_ENERGY_TYPE = {
  DIESEL: "diesel",
  PETROL: "petrol",
  NATURALGAS: "naturalGas",
  HEATINGOIL: "heatingOil",
  ELECTRICITY: "electricity",
  WOODCHIP_HEATER: "woodchip_heater",
} as const;

export const FERTILIZER_UNITS = {
  TONNES: "tonnes",
  CUBIC_METERS: "cubic_meters",
} as const;

export const PLANT_PROTECTION_UNITS = {
  KILOGRAMS: "kilograms",
  LITERS: "liters",
} as const;

export const DAIRY_FEED_ADDITIVE_UNITS = {
  KILOGRAMS: "kilograms",
  LITERS: "liters",
  CUBIC_METERS: "cubic_meters",
  TONNES: "tonnes",
} as const;

export const BEEF_FEED_ADDITIVE_UNITS = {
  KILOGRAMS: "kilograms",
  LITERS: "liters",
  CUBIC_METERS: "cubic_meters",
  TONNES: "tonnes",
} as const;

export const MANURE_ADDITIVE_UNITS = {
  KILOGRAMS: "kilograms",
  LITERS: "liters",
} as const;
export type ENUM_MANURE_ADDITIVE_UNITS = (typeof MANURE_ADDITIVE_UNITS)[keyof typeof MANURE_ADDITIVE_UNITS];

// The manure-additive catalog, shared across every animal type (one api endpoint, one table).
export type ManureAdditive = {
  id: string;
  enabled: boolean;
  translationSlug: string;
};

export const FARM_FOOTPRINTING_PORK_MANURE_ADDITIVE_ACTIVE = {
  YES: "yes",
  NO: "no",
} as const;

export const FARM_FOOTPRINTING_PIGLET_MANURE_ADDITIVE_ACTIVE = {
  YES: "yes",
  NO: "no",
} as const;

export const DAIRY_FEED_PRODUCTION_UNITS = {
  KILOGRAMS: "kilograms",
  LITERS: "liters",
  CUBIC_METERS: "cubic_meters",
  TONNES: "tonnes",
} as const;

export const FARM_FOOTPRINTING_DRYING_ENERGY_TYPE_SUFFIXES = {
  [FARM_FOOTPRINTING_DRYING_ENERGY_TYPE.DIESEL]: "L",
  [FARM_FOOTPRINTING_DRYING_ENERGY_TYPE.PETROL]: "L",
  [FARM_FOOTPRINTING_DRYING_ENERGY_TYPE.NATURALGAS]: "m3/t",
  [FARM_FOOTPRINTING_DRYING_ENERGY_TYPE.HEATINGOIL]: "L",
  [FARM_FOOTPRINTING_DRYING_ENERGY_TYPE.ELECTRICITY]: "kWh",
  [FARM_FOOTPRINTING_DRYING_ENERGY_TYPE.WOODCHIP_HEATER]: "kg",
} as const;

export const FOOTPRINTING_BANNER_KEYS = {
  FIRST: "first",
  MANY: "many",
  ALL: "all",
  UNSUBMITTED_ASSESSMENTS: "unsubmitted-assessments",
  SUBMITTED_ASSESSMENTS: "submitted-assessments",
} as const;

export const FARM_FOOTPRINTING_PORK_MOORLAND = {
  YES: "yes",
  NO: "no",
} as const;

export const FARM_FOOTPRINTING_PIGLET_MOORLAND = {
  YES: "yes",
  NO: "no",
} as const;

export interface AppliedPracticeBase {
  applicationId: string | null;
}

export type AppliedPractice<T> = AppliedPracticeBase & T;

export type ENUM_FARM_FOOTPRINTING_DAIRY_MILK_PRODUCTION =
  (typeof FARM_FOOTPRINTING_DAIRY_MILK_PRODUCTION)[keyof typeof FARM_FOOTPRINTING_DAIRY_MILK_PRODUCTION];

export type ENUM_FARM_FOOTPRINTING_DAIRY_MOORLAND = (typeof FARM_FOOTPRINTING_DAIRY_MOORLAND)[keyof typeof FARM_FOOTPRINTING_DAIRY_MOORLAND];
export type ENUM_FARM_FOOTPRINTING_BEEF_MOORLAND = (typeof FARM_FOOTPRINTING_BEEF_MOORLAND)[keyof typeof FARM_FOOTPRINTING_BEEF_MOORLAND];
export type ENUM_FARM_FOOTPRINTING_DAIRY_MANURE_ADDITIVE_ACTIVE =
  (typeof FARM_FOOTPRINTING_DAIRY_MANURE_ADDITIVE_ACTIVE)[keyof typeof FARM_FOOTPRINTING_DAIRY_MANURE_ADDITIVE_ACTIVE];

export type ENUM_FARM_FOOTPRINTING_DAIRY_KNOWN_COW_WEIGHT =
  (typeof FARM_FOOTPRINTING_DAIRY_KNOWN_COW_WEIGHT)[keyof typeof FARM_FOOTPRINTING_DAIRY_KNOWN_COW_WEIGHT];

export type ENUM_FARM_FOOTPRINTING_DAIRY_COWS_IN_PASTURE =
  (typeof FARM_FOOTPRINTING_DAIRY_COWS_IN_PASTURE)[keyof typeof FARM_FOOTPRINTING_DAIRY_COWS_IN_PASTURE];

export type ENUM_FARM_FOOTPRINTING_DAIRY_COW_BODY_CONDITION =
  (typeof FARM_FOOTPRINTING_DAIRY_COW_BODY_CONDITION)[keyof typeof FARM_FOOTPRINTING_DAIRY_COW_BODY_CONDITION];

export type ENUM_FARM_FOOTPRINTING_DAIRY_FEED_ADDITIVES = (typeof FARM_FOOTPRINTING_DAIRY_FEED_ADDITIVES)[keyof typeof FARM_FOOTPRINTING_DAIRY_FEED_ADDITIVES];

export type ENUM_FARM_FOOTPRINTING_DAIRY_CALVES_FEED = (typeof FARM_FOOTPRINTING_DAIRY_CALVES_FEED)[keyof typeof FARM_FOOTPRINTING_DAIRY_CALVES_FEED];

export type ENUM_FARM_FOOTPRINTING_DAIRY_FEED_PRODUCTION_CROP_USAGE =
  (typeof FARM_FOOTPRINTING_DAIRY_FEED_PRODUCTION_CROP_USAGE)[keyof typeof FARM_FOOTPRINTING_DAIRY_FEED_PRODUCTION_CROP_USAGE];

export type ENUM_FARM_FOOTPRINTING_OVERALL_SOIL_TYPE = (typeof FARM_FOOTPRINTING_OVERALL_SOIL_TYPE)[keyof typeof FARM_FOOTPRINTING_OVERALL_SOIL_TYPE];

export type ENUM_FARM_FOOTPRINTING_HAS_LAND_CHANGED = (typeof FARM_FOOTPRINTING_HAS_LAND_CHANGED)[keyof typeof FARM_FOOTPRINTING_HAS_LAND_CHANGED];

export type ENUM_FARM_FOOTPRINTING_AVERAGE_SOIL_SATURATION =
  (typeof FARM_FOOTPRINTING_AVERAGE_SOIL_SATURATION)[keyof typeof FARM_FOOTPRINTING_AVERAGE_SOIL_SATURATION];

export type ENUM_FARM_FOOTPRINTING_DRYING_ENERGY_TYPE = (typeof FARM_FOOTPRINTING_DRYING_ENERGY_TYPE)[keyof typeof FARM_FOOTPRINTING_DRYING_ENERGY_TYPE];

export type ENUM_FERTILIZER_UNITS = (typeof FERTILIZER_UNITS)[keyof typeof FERTILIZER_UNITS];
export type ENUM_PLANT_PROTECTION_UNITS = (typeof PLANT_PROTECTION_UNITS)[keyof typeof PLANT_PROTECTION_UNITS];
export type ENUM_DAIRY_FEED_ADDITIVE_UNITS = (typeof DAIRY_FEED_ADDITIVE_UNITS)[keyof typeof DAIRY_FEED_ADDITIVE_UNITS];
export type ENUM_BEEF_FEED_ADDITIVE_UNITS = (typeof BEEF_FEED_ADDITIVE_UNITS)[keyof typeof BEEF_FEED_ADDITIVE_UNITS];
export type ENUM_DAIRY_FEED_PRODUCTION_UNITS = (typeof DAIRY_FEED_PRODUCTION_UNITS)[keyof typeof DAIRY_FEED_PRODUCTION_UNITS];

export const PORK_STABLE_TYPE = {
  OPEN_AIR_DEEP_LITTER_STRAW: "open_air_deep_litter_straw",
  OPEN_AIR_SLURRY: "open_air_slurry",
  SOLID_WALKING_SURFACES_BEDDING: "solid_walking_surfaces_bedding",
  DEEP_LITTER: "deep_litter",
  OPEN_AIR_DEEP_LITTER_STRAW_NO_OUTDOOR: "open_air_deep_litter_straw_no_outdoor",
  PARTIALLY_SLATTED_SLURRY_INSULATED: "partially_slatted_slurry_insulated",
  FULLY_SLATTED_SLURRY: "fully_slatted_slurry",
  ACTIVE_STABLE_OUTDOOR_PASTURE: "active_stable_outdoor_pasture",
  FREE_RANGE: "free_range",
} as const;
export type ENUM_PORK_STABLE_TYPE = (typeof PORK_STABLE_TYPE)[keyof typeof PORK_STABLE_TYPE];

export const PIGLET_STABLE_TYPE = {
  OPEN_AIR_DEEP_LITTER_STRAW: "open_air_deep_litter_straw",
  OPEN_AIR_SLURRY: "open_air_slurry",
  SOLID_WALKING_SURFACES_BEDDING: "solid_walking_surfaces_bedding",
  DEEP_LITTER: "deep_litter",
  OPEN_AIR_DEEP_LITTER_STRAW_NO_OUTDOOR: "open_air_deep_litter_straw_no_outdoor",
  PARTIALLY_SLATTED_SLURRY_INSULATED: "partially_slatted_slurry_insulated",
  FULLY_SLATTED_SLURRY: "fully_slatted_slurry",
  ACTIVE_STABLE_OUTDOOR_PASTURE: "active_stable_outdoor_pasture",
  FREE_RANGE: "free_range",
} as const;
export type ENUM_PIGLET_STABLE_TYPE = (typeof PIGLET_STABLE_TYPE)[keyof typeof PIGLET_STABLE_TYPE];

export const PORK_AMMONIA_FILTRATION_EFFICIENCY = {
  VERY_LOW: "very_low",
  LOW: "low",
  MEDIUM: "medium",
  GOOD: "good",
  HIGH: "high",
  VERY_HIGH: "very_high",
} as const;
export type ENUM_PORK_AMMONIA_FILTRATION_EFFICIENCY = (typeof PORK_AMMONIA_FILTRATION_EFFICIENCY)[keyof typeof PORK_AMMONIA_FILTRATION_EFFICIENCY];

export const PIGLET_AMMONIA_FILTRATION_EFFICIENCY = {
  VERY_LOW: "very_low",
  LOW: "low",
  MEDIUM: "medium",
  GOOD: "good",
  HIGH: "high",
  VERY_HIGH: "very_high",
} as const;
export type ENUM_PIGLET_AMMONIA_FILTRATION_EFFICIENCY = (typeof PIGLET_AMMONIA_FILTRATION_EFFICIENCY)[keyof typeof PIGLET_AMMONIA_FILTRATION_EFFICIENCY];

export const PORK_PIGLET_SOURCING_TYPE = {
  SELF_PRODUCED: "self_produced",
  PURCHASED: "purchased",
} as const;
export type ENUM_PORK_PIGLET_SOURCING_TYPE = (typeof PORK_PIGLET_SOURCING_TYPE)[keyof typeof PORK_PIGLET_SOURCING_TYPE];

export const CFP_REFRIGERANT_SUBAPPLICATION = {
  DOMESTIC_REFRIGERATION: "Domestic refrigeration",
  COMMERCIAL_REFRIGERATION_STANDALONE: "Commercial refrigeration (standalone)",
  COMMERCIAL_REFRIGERATION_MEDIUM_AND_LARGE: "Commercial refrigeration (medium and large)",
  TRANSPORT_REFRIGERATION: "Transport refrigeration",
  INDUSTRIAL_REFRIGERATION: "Industrial refrigeration",
  CHILLERS: "Chillers",
  STATIONARY_AIR_CONDITIONING: "Stationary air conditioning",
  MOBILE_AC_MARITIME: "Mobile air conditioning-maritime",
  MOBILE_AC_RAILWAY: "Mobile air conditioning-railway",
  MOBILE_AC_BUSSES: "Mobile air conditioning-busses",
  MOBILE_AC_OTHER: "Mobile air conditioning-other",
} as const;

export type ENUM_CFP_REFRIGERANT_SUBAPPLICATION = (typeof CFP_REFRIGERANT_SUBAPPLICATION)[keyof typeof CFP_REFRIGERANT_SUBAPPLICATION];

export const CFP_REFRIGERANT_RECOVERY_TYPE = {
  LANDFILL: "Landfill",
  PARTIAL_RECOVERY: "Partial refrigerant recovery",
  FULL_RECOVERY: "Full refrigerant recovery",
  UNKNOWN: "Unknown",
} as const;

export type ENUM_CFP_REFRIGERANT_RECOVERY_TYPE = (typeof CFP_REFRIGERANT_RECOVERY_TYPE)[keyof typeof CFP_REFRIGERANT_RECOVERY_TYPE];

export interface AggregatedFarmFootprint {
  id: string;
  year: number;
  farmId: string;
  isSubmitted: boolean;
  isVerified?: boolean;
  arableLandActive?: boolean | null;
  overallSoilType: ENUM_FARM_FOOTPRINTING_OVERALL_SOIL_TYPE | null;
  averageSoilSaturation: ENUM_FARM_FOOTPRINTING_AVERAGE_SOIL_SATURATION | null;
  electricityKwh: number | null;
  dairyElectricityKwh: number | null;
  beefElectricityKwh?: number | null;
  porkElectricityKwh?: number | null;
  renewablesPct: number | null;
  dieselL: number | null;
  petrolL: number | null;
  naturalGasM3: number | null;
  heatingOilL: number | null;
  hasLandChanged: ENUM_FARM_FOOTPRINTING_HAS_LAND_CHANGED | null;
  landUseChangeUsage: Array<{
    id: string;
    hasLandChanged: ENUM_FARM_FOOTPRINTING_HAS_LAND_CHANGED | null;
    areaConvertedHa: number | null;
    yearOfConversion: number | null;
    previousLandUseChangeId: string | null;
    previousLandUseType: string | null;
    nextLandUseChangeId: string | null;
    nextLandUseType: string | null;
  }>;
  comment?: string | null;
}

export interface AggregatedCropFootprint {
  id: string;
  year: number;
  cropId: string;
  farmId: string;
  isSubmitted: boolean;
  isVerified?: boolean;
  totalWeightPerHa: number | null;
  totalWeightPerHaUnit: HarvestUnitValue | null;
  totalWeightTs: number | null;
  normalizedWeightTs: number | null;
  preDryingMoistureContentPct: number | null;
  byproductPct: number | null;
  seedWeightKgPerHa: number | null;
  soilPh: number | null;
  fieldIds: string[];
  totalFieldHectares: number;
  tillageActive: boolean | null;
  seedingActive: boolean | null;
  tillageUsage: Array<
    AppliedPractice<{
      implementedOnHa: number | null;
      tillageDepth: number | null;
    }>
  >;
  seedingUsage: Array<
    AppliedPractice<{
      implementedOnHa: number | null;
    }>
  >;
  mineralFertilizerActive: boolean | null;
  organicFertilizerActive: boolean | null;
  mineralFertilizerUsage: Array<
    AppliedPractice<{
      amount: number | null;
      amountUnit: ENUM_FERTILIZER_UNITS | null;
    }>
  >;
  organicFertilizerUsage: Array<
    AppliedPractice<{
      amount: number | null;
      amountUnit: ENUM_FERTILIZER_UNITS | null;
      nValue: number | null;
    }>
  >;
  limeT: number | null;
  plantProtectionActive: boolean | null;
  plantProtectionUsage: Array<
    AppliedPractice<{
      amount: number | null;
      amountUnit: ENUM_PLANT_PROTECTION_UNITS | null;
      activeIngredientsPct: number | null;
    }>
  >;
  irrigationActive: boolean | null;
  waterUsedM3: number | null;
  irrigationApplicationMethod: ENUM_CFP_CROP_IRRIGATION_APPLICATION_METHOD | null;
  irrigationWaterSource: ENUM_CFP_CROP_IRRIGATION_WATER_SOURCE | null;
  irrigationPumpPowerSource: ENUM_CFP_CROP_IRRIGATION_PUMP_POWER_SOURCE | null;
  irrigationAreaPct: number | null;
  dryingActive: boolean | null;
  dryingUsages: Array<{
    energyType: ENUM_FARM_FOOTPRINTING_DRYING_ENERGY_TYPE | null;
    fuelAmount: number | null;
    renewableEnergyPct: number | null;
  }>;
  cropSaleActive: boolean | null;
  cropSales: Array<{
    purchaserId: string | null;
    purchaserName: string | null;
    purchaserCountryCode: COUNTRY_CODE | null;
    purchaserPostCode: string | null;
    soldYieldTs: number | null;
  }>;
  reviewFieldIds?: string[];
  comment?: string | null;
  residueManagementPractice?: ENUM_CFP_CROP_RESIDUE_MANAGEMENT_PRACTICE | null;
}

export interface AggregatedDairyFootprint {
  id: string;
  year: number;
  farmId: string;
  isSubmitted: boolean;
  isVerified: boolean;
  milkProduction: ENUM_FARM_FOOTPRINTING_DAIRY_MILK_PRODUCTION | null;
  milkYieldKgPerCow: number | null;
  fcpmCorrectedMilkYieldKgPerCow: number | null;
  fatContentPct: number | null;
  proteinContentPct: number | null;
  cowsInPastureActive: ENUM_FARM_FOOTPRINTING_DAIRY_COWS_IN_PASTURE | null;
  cowsInPasture: number | null;
  daysInPasturePerYear: number | null;
  avgHoursInPasturePerDay: number | null;
  forageLossPct: number | null;
  feedAdditiveActive: boolean | null;
  grasslandFertiliserActive: ENUM_DAIRY_GRASSLAND_FERTILISER_ACTIVE | null;
  moorlandActive?: ENUM_FARM_FOOTPRINTING_DAIRY_MOORLAND | null;
  fodderProductionAreaHa?: number | null;
  annualWaterLevelCm?: number | null;
  amountStoredGasTankPct: number | null;
  refrigerantsActive: ENUM_DAIRY_REFRIGERANTS_ACTIVE | null;
  refrigerantUsage: Array<{
    id: string;
    refrigerantType: string | null;
    amountUsedKg: number | null;
    subapplication: ENUM_CFP_REFRIGERANT_SUBAPPLICATION | null;
    unitCount: number | null;
    unitAge: number | null;
    equipmentEol: boolean | null;
    refrigerantRecoveryType: ENUM_CFP_REFRIGERANT_RECOVERY_TYPE | null;
  }>;
  herdDetails: {
    calvesAmount?: number | null;
    avgCowLosses?: number | null;
    avgCowUsefulLifeMonths?: number | null;
    cowWeightKnown?: ENUM_FARM_FOOTPRINTING_DAIRY_KNOWN_COW_WEIGHT | null;
    averageCowWeightKg?: number | null;
    estimatedCowBodyCondition?: ENUM_FARM_FOOTPRINTING_DAIRY_COW_BODY_CONDITION | null;
    matFeedPct?: number | null;
    wholeMilkFeedPct?: number | null;
    ageOfFirstCalvingMonths?: number | null;
    timeBetweenCalvingDays?: number | null;
  };
  herdSize: Array<{
    cowTypeId: string | null;
    cowCount: number | null;
  }>;
  cowSales: {
    cowSalesInPoorCondition: number | null;
    cowSalesForLightManufacturing: number | null;
    cowSalesForStandardManufacturing: number | null;
    cowSalesInGoodCondition: number | null;
    cowSalesInPremiumCondition: number | null;
  };
  feedAdditiveUsage: Array<{
    additiveId: string | null;
    additiveType: string | null;
    amount: number | null;
    amountUnit: ENUM_DAIRY_FEED_ADDITIVE_UNITS | null;
  }>;
  feedInformation: Array<{
    id: string;
    feedTypeId: string | null;
    isSelfProduced: boolean | null;
    totalAmountFedTs: number | null;
    fedToCowsPct: number | null;
    fedToCalvesPct: number | null;
    silagePct: number | null;
    isSilage: boolean | null;
    energyValue: number | null;
    energyUnit: ENUM_DAIRY_FEED_ENERGY_UNIT | null;
    energyMjNelPerKgDm: number | null;
    dryMatterPct: number | null;
    ndfPct: number | null;
    emissionFactorKgCo2Eq: number | null;
    feedProduction?: {
      cropUsedForFeed: ENUM_FARM_FOOTPRINTING_DAIRY_FEED_PRODUCTION_CROP_USAGE | null;
      cropYieldTPerHa: number | null;
      dieselConsumptionL: number | null;
      energyUsedKwh: number | null;
      mineralNitrogenKgHa: number | null;
      mineralPhosphorusKgHa: number | null;
      mineralPotassiumKgHa: number | null;
      nitrogenOrganicFertKgHa: number | null;
      totalLimeAppliedKgCaCo3PerHa: number | null;
      totalPlantProtectionApplied: number | null;
      totalPlantProtectionAppliedUnit: ENUM_DAIRY_FEED_PRODUCTION_UNITS | null;
      totalWaterUsedIrregationLPerHa: number | null;
      soldOffFarmPct: number | null;
      otherUsesPct: number | null;
    } | null;
  }>;
  manureStorageUsage: Array<{
    storageId: string | null;
    storageType: string | null;
    amountStoredPct: number | null;
  }>;
  manureAdditiveUsage: Array<{
    additiveId: string | null;
    additiveType: string | null;
    amountUsedM3: number | null;
    amountUsedM3Unit: ENUM_MANURE_ADDITIVE_UNITS | null;
    enrichedSlurryPct: number | null;
  }>;
  comment?: string | null;
}

export interface AggregatedPorkFootprint {
  id: string;
  year: number;
  farmId: string;
  isSubmitted: boolean;
  isVerified: boolean;
  yieldKgs: number | null;
  liveWeight: number | null;
  manureAdditiveActive: ENUM_PORK_FOOTPRINTING_MANURE_ADDITIVE_ACTIVE | null;
  moorlandActive: ENUM_PORK_FOOTPRINTING_MOORLAND_ACTIVE | null;
  moorlandAreaForFodderProduction: number | null;
  moorlandAnnualWaterLevel: number | null;
  herdDetails: {
    averageHerdSize: number | null;
    rearingPeriodDays: number | null;
    pigLossPct: number | null;
  };
  pigletSourcing: {
    sourcingType: ENUM_PORK_PIGLET_SOURCING_TYPE;
    sourceFarms: Array<{
      id: string;
      sourceFarmName: string | null;
      vvvoIdentifier: string | null;
      quantityPurchased: number | null;
      averageWeightKg: number | null;
    }>;
  } | null;
  stables: Array<{
    id: string;
    stableType: ENUM_PORK_STABLE_TYPE;
    stableSize: number | null;
    hasAmmoniaFiltration: boolean | null;
    ammoniaFiltrationEfficiency: ENUM_PORK_AMMONIA_FILTRATION_EFFICIENCY | null;
  }>;
  feedInformation: Array<{
    id: string;
    feedId: string | null;
    isSelfProduced: boolean | null;
    totalAmountFedTs: number | null;
    silagePct: number | null;
    isSilage: boolean;
    energyValue: number | null;
    dryMatterPct: number | null;
    feedProduction?: {
      cropUsedForFeed: ENUM_FARM_FOOTPRINTING_DAIRY_FEED_PRODUCTION_CROP_USAGE | null;
      cropYieldTPerHa: number | null;
      dieselConsumptionLHa: number | null;
      energyUsedKwhHa: number | null;
      mineralNitrogenKgHa: number | null;
      mineralPhosphorusKgHa: number | null;
      mineralPotassiumKgHa: number | null;
      nitrogenOrganicFertKgHa: number | null;
      totalLimeAppliedKgCaCo3PerHa: number | null;
      totalPlantProtectionApplied: number | null;
      totalPlantProtectionAppliedUnit: ENUM_DAIRY_FEED_PRODUCTION_UNITS | null;
      totalWaterUsedIrregationLPerHa: number | null;
      soldOffFarmPct: number | null;
      fedToPigsPct: number | null;
      otherUsesPct: number | null;
    } | null;
  }>;
  manureStorageUsage: Array<{
    storageId: string;
    amountStoredPct: number | null;
  }>;
  manureAdditiveUsage: Array<{
    additiveId: string;
    amountUsedM3: number | null;
    amountUsedM3Unit: ENUM_MANURE_ADDITIVE_UNITS | null;
    enrichedSlurryPct: number | null;
  }>;
  comment: string | null;
}

export interface AggregatedPigletFootprint {
  id: string;
  year: number;
  farmId: string;
  isSubmitted: boolean;
  isVerified: boolean;
  averageSalesWeightKg: number | null;
  averageNumberOfLitters: number | null;
  averageLitterSize: number | null;
  manureAdditiveActive: ENUM_PIGLET_FOOTPRINTING_MANURE_ADDITIVE_ACTIVE | null;
  moorlandActive: ENUM_PIGLET_FOOTPRINTING_MOORLAND_ACTIVE | null;
  moorlandAreaForFodderProduction: number | null;
  moorlandAnnualWaterLevel: number | null;
  herdDetails: {
    averageNumberOfSows: number | null;
    yearlyReplacementRatePct: number | null;
    sucklingPigletLossRatePct: number | null;
    rearingLossPct: number | null;
    soldSucklingPigsPct: number | null;
  };
  stables: Array<{
    id: string;
    stableType: ENUM_PIGLET_STABLE_TYPE;
    stableSize: number | null;
    hasAmmoniaFiltration: boolean | null;
    ammoniaFiltrationEfficiency: ENUM_PIGLET_AMMONIA_FILTRATION_EFFICIENCY | null;
  }>;
  feedInformation: Array<{
    id: string;
    feedId: string | null;
    isSelfProduced: boolean | null;
    totalAmountFedTs: number | null;
    silagePct: number | null;
    isSilage: boolean;
    energyValue: number | null;
    dryMatterPct: number | null;
    feedProduction?: {
      cropUsedForFeed: ENUM_FARM_FOOTPRINTING_DAIRY_FEED_PRODUCTION_CROP_USAGE | null;
      cropYieldTPerHa: number | null;
      dieselConsumptionLHa: number | null;
      energyUsedKwhHa: number | null;
      mineralNitrogenKgHa: number | null;
      mineralPhosphorusKgHa: number | null;
      mineralPotassiumKgHa: number | null;
      nitrogenOrganicFertKgHa: number | null;
      totalLimeAppliedKgCaCo3PerHa: number | null;
      totalPlantProtectionApplied: number | null;
      totalPlantProtectionAppliedUnit: ENUM_DAIRY_FEED_PRODUCTION_UNITS | null;
      totalWaterUsedIrregationLPerHa: number | null;
      soldOffFarmPct: number | null;
      fedToPigletsPct: number | null;
      otherUsesPct: number | null;
    } | null;
  }>;
  manureStorageUsage: Array<{
    storageId: string;
    amountStoredPct: number | null;
  }>;
  manureAdditiveUsage: Array<{
    additiveId: string;
    amountUsedM3: number | null;
    amountUsedM3Unit: ENUM_MANURE_ADDITIVE_UNITS | null;
    enrichedSlurryPct: number | null;
  }>;
  comment: string | null;
}

export type FootprintingFormCompletionStats<T extends Pick<AggregatedFarmFootprint, "isSubmitted"> = Pick<AggregatedFarmFootprint, "isSubmitted">> = {
  footprint?: T;
  completionPct: number;
  pagesCompleted: string[];
  pagesIncomplete: string[];
  completionPayout?: number;
  year: number;
};

export type FeedProducingFootprint = {
  feedProductionCompletion?: Record<string, boolean>;
};

export type FarmFootprintReturnBody = FootprintingFormCompletionStats<AggregatedFarmFootprint> & {
  farmId: string;
};

export type CropFootprintReturnBody = FootprintingFormCompletionStats<AggregatedCropFootprint> & {
  cropId: string;
  farmId: string;
};

export type DairyFootprintReturnBody = FootprintingFormCompletionStats<AggregatedDairyFootprint> & FeedProducingFootprint;
export type PorkFootprintReturnBody = FootprintingFormCompletionStats<AggregatedPorkFootprint> & FeedProducingFootprint;
export type PigletFootprintReturnBody = FootprintingFormCompletionStats<AggregatedPigletFootprint> & FeedProducingFootprint;
export type BeefFootprintReturnBody = FootprintingFormCompletionStats<AggregatedBeefFootprint> & FeedProducingFootprint;

export interface AggregatedBeefFootprint {
  id: string;
  year: number;
  farmId: string;
  isSubmitted: boolean;
  isVerified: boolean;
  beefYieldKgPerAnimal: number | null;
  meatProduction: ENUM_FARM_FOOTPRINTING_BEEF_MEAT_PRODUCTION | null;
  cowsInPastureActive: ENUM_FARM_FOOTPRINTING_BEEF_COWS_IN_PASTURE | null;
  cowsInPasture: number | null;
  daysInPasturePerYear: number | null;
  avgHoursInPasturePerDay: number | null;
  forageLossPct: number | null;
  feedAdditiveActive: ENUM_FARM_FOOTPRINTING_BEEF_FEED_ADDITIVE_ACTIVE | null;
  grasslandFertiliserActive: ENUM_BEEF_GRASSLAND_FERTILISER_ACTIVE | null;
  moorlandActive: ENUM_FARM_FOOTPRINTING_BEEF_MOORLAND | null;
  fodderProductionAreaHa: number | null;
  annualWaterLevelCm: number | null;
  amountStoredGasTankPct: number | null;
  manureAdditiveActive: ENUM_FARM_FOOTPRINTING_BEEF_MANURE_ADDITIVE_ACTIVE | null;
  comment: string | null;
  herdSize: Array<{ cowTypeId: string; cowCount: number }>;
}

export const FARM_FOOTPRINTING_BEEF_MEAT_PRODUCTION = {
  LIVE_WEIGHT: "live_weight",
  SLAUGHTER_WEIGHT: "slaughter_weight",
} as const;

export type ENUM_FARM_FOOTPRINTING_BEEF_MEAT_PRODUCTION = (typeof FARM_FOOTPRINTING_BEEF_MEAT_PRODUCTION)[keyof typeof FARM_FOOTPRINTING_BEEF_MEAT_PRODUCTION];

export interface FootprintingBeefPrimaryData {
  id: string;
  farmId: string;
  year: number;
  isSubmitted?: boolean;
  isVerified?: boolean;
  beefYieldKg?: number | null;
  meatProduction?: ENUM_FARM_FOOTPRINTING_BEEF_MEAT_PRODUCTION | null;
  feedAdditiveActive?: ENUM_FARM_FOOTPRINTING_BEEF_FEED_ADDITIVE_ACTIVE | null;
  grasslandFertiliserActive?: ENUM_BEEF_GRASSLAND_FERTILISER_ACTIVE | null;
  cowsInPastureActive?: ENUM_FARM_FOOTPRINTING_BEEF_COWS_IN_PASTURE | null;
  cowsInPasture?: number | null;
  daysInPasturePerYear?: number | null;
  avgHoursInPasturePerDay?: number | null;
  forageLossPct?: number | null;
  moorlandActive?: ENUM_FARM_FOOTPRINTING_BEEF_MOORLAND | null;
  fodderProductionAreaHa?: number | null;
  annualWaterLevelCm?: number | null;
  amountStoredGasTankPct?: number | null;
  manureAdditiveActive?: ENUM_FARM_FOOTPRINTING_BEEF_MANURE_ADDITIVE_ACTIVE | null;
  comment?: string | null;
}

export interface FootprintingBeefHerdSize {
  id: string;
  primaryDataId: string;
  cowTypeId: string;
  cowCount: number;
}

export interface FootprintingBeefHerdDetails {
  id?: string;
  calvesAmount?: number | null;
  avgCowLosses?: number | null;
  avgCowUsefulLifeMonths?: number | null;
  fatteningStartWeightKg?: number | null;
  calfMilkFeedingPeriodDays?: number | null;
  rearingTimeDays?: number | null;
}

export interface UploadableBeefHerdSize {
  id?: string;
  cowTypeId: string;
  cowCount: number;
}

export interface UploadableBeefFeedAdditive {
  id?: string;
  feedAdditiveId: string;
  amount: number | null;
  amountUnit: ENUM_BEEF_FEED_ADDITIVE_UNITS | null;
}

export interface UploadableBeefFeedInformation {
  id?: string;
  feedTypeId?: string | null;
  isSelfProduced: boolean | null;
  totalAmountFedTs?: number | null;
  silagePct?: number | null;
  energyValue?: number | null;
  energyUnit?: ENUM_DAIRY_FEED_ENERGY_UNIT | null;
  dryMatterPct?: number | null;
  emissionFactorKgCo2Eq?: number | null;
  cowsPct?: number | null;
  calvesPct?: number | null;
}

export interface FootprintingBeefFeedInformation {
  id: string;
  primaryDataId: string;
  isSelfProduced: boolean | null;
  feedTypeId: string | null;
  totalAmountFedTs: number | null;
  silagePct: number | null;
  energyValue: number | null;
  energyUnit: ENUM_DAIRY_FEED_ENERGY_UNIT | null;
  dryMatterPct: number | null;
  emissionFactorKgCo2Eq: number | null;
  cowsPct: number | null;
  calvesPct: number | null;
}

export interface FootprintingBeefGrasslandFertiliser {
  id: string;
  primaryDataId: string;
  fertiliserProductId: string;
  totalAmountApplied: number | null;
  totalAmountUnit: ENUM_FERTILIZER_UNITS | null;
}

export type FootprintingBeefGrasslandFertiliserPayload = {
  selectedGrasslandFertilisers: UploadableGrasslandFertiliser[];
};

export interface UploadableBeefManureStorage {
  id?: string;
  manureStorageId: string;
  amountStoredPct: number | null;
}

export interface FootprintingFarmPrimaryData {
  id: string;
  isSubmitted?: boolean;
  isVerified?: boolean;
  farmId: string;
  year: number;
  arableLandActive?: boolean | null;
  overallSoilType?: ENUM_FARM_FOOTPRINTING_OVERALL_SOIL_TYPE | null;
  averageSoilSaturation?: ENUM_FARM_FOOTPRINTING_AVERAGE_SOIL_SATURATION | null;
  hasLandChanged?: ENUM_FARM_FOOTPRINTING_HAS_LAND_CHANGED | null;
  comment?: string | null;
}

export interface FootprintingFarmResourceConsumption {
  id?: string;
  footprintingFarmPrimaryDataId: string;
  electricityKwh?: number | null;
  dairyElectricityKwh?: number | null;
  beefElectricityKwh?: number | null;
  porkElectricityKwh?: number | null;
  renewablesPct?: number | null;
  dieselL?: number | null;
  petrolL?: number | null;
  naturalGasM3?: number | null;
  heatingOilL?: number | null;
  heatPumpKwh?: number | null;
  biogasKwh?: number | null;
  woodChipsKwh?: number | null;
}

export interface UploadableRefrigerant {
  id?: string;
  emissionFactorRefrigerantId: string;
  amountKg: number | null;
  subapplication?: ENUM_CFP_REFRIGERANT_SUBAPPLICATION | null;
  unitCount?: number | null;
  unitAge?: number | null;
  equipmentEol?: boolean | null;
  refrigerantRecoveryType?: ENUM_CFP_REFRIGERANT_RECOVERY_TYPE | null;
}

export interface FootprintingDairyRefrigerantUsage {
  id: string;
  primaryDataId: string;
  emissionFactorRefrigerantId: string;
  amountKg: number | null;
  subapplication?: ENUM_CFP_REFRIGERANT_SUBAPPLICATION | null;
  unitCount?: number | null;
  unitAge?: number | null;
  equipmentEol?: boolean | null;
  refrigerantRecoveryType?: ENUM_CFP_REFRIGERANT_RECOVERY_TYPE | null;
}

export interface UploadableLandUseChange {
  id?: string;
  previousLandUseChangeId?: string | null;
  nextLandUseChangeId?: string | null;
  yearOfConversion?: number | null;
  areaConvertedHa?: number | null;
}

export interface LandUseChangeRequestPayload {
  farmId: string;
  selectedLandUseChange: UploadableLandUseChange[];
}

export interface FarmLandUseChange {
  id: string;
  farmId: string;
  previousLandUseChangeId: string | null;
  nextLandUseChangeId: string | null;
  yearOfConversion: number | null;
  areaConvertedHa: number | null;
}

export interface UploadablePlantProtection {
  id?: string;
  practicesPlantProtectionId: string;
  amount: number | null;
  amountUnit: ENUM_PLANT_PROTECTION_UNITS | null;
}

export interface FootprintingFarmCropPlantProtectionPayload {
  footprintingFarmPrimaryDataId: string;
  selectedPlantProtection: UploadablePlantProtection[];
}

export interface FootprintingCropPrimaryData {
  id: string;
  isSubmitted?: boolean;
  isVerified?: boolean;
  farmId: string;
  cropId: string;
  year: number;
  totalWeightPerHa?: number | null;
  totalWeightPerHaUnit?: HarvestUnitValue | null;
  preDryingMoistureContentPct?: number | null;
  byproductPct?: number | null;
  seedWeightKgPerHa?: number | null;
  soilPh?: number | null;
  waterUsedM3?: number | null;
  fieldIds?: string[];
  postDryingMoistureContentPct?: number | null;
  driedWeightTs?: number | null;
  limeT?: number | null;
  mineralFertilizerActive?: boolean | null;
  organicFertilizerActive?: boolean | null;
  plantProtectionActive?: boolean | null;
  tillageActive?: boolean | null;
  seedingActive?: boolean | null;
  irrigationActive?: boolean | null;
  irrigationApplicationMethod?: ENUM_CFP_CROP_IRRIGATION_APPLICATION_METHOD | null;
  irrigationWaterSource?: ENUM_CFP_CROP_IRRIGATION_WATER_SOURCE | null;
  irrigationPumpPowerSource?: ENUM_CFP_CROP_IRRIGATION_PUMP_POWER_SOURCE | null;
  irrigationAreaPct?: number | null;
  dryingActive?: boolean | null;
  cropSaleActive?: boolean | null;
  reviewFieldIds?: string[];
  comment?: string | null;
  residueManagementPractice?: ENUM_CFP_CROP_RESIDUE_MANAGEMENT_PRACTICE | null;
}

export interface FootprintingCropDrying {
  id?: string;
  energyType: ENUM_FARM_FOOTPRINTING_DRYING_ENERGY_TYPE | null;
  fuelAmount: number | null;
  renewableEnergyPct: number | null;
}

export interface FootprintingCropSeeding {
  id?: string;
  footprintingFarmCropPrimaryDataId: string;
  practicesSeedingId: string;
  implementedOnHa: number | null;
}

export interface FootprintingCropTillage {
  id?: string;
  footprintingFarmCropPrimaryDataId: string;
  practicesTillageId: string;
  implementedOnHa: number | null;
  tillageDepth: number | null;
}

export interface FootprintingCropFertilizer {
  id?: string;
  fertilizerId: string;
  amount: number | null;
  amountUnit: ENUM_FERTILIZER_UNITS | null;
  nValue?: number | null;
}

export interface PurchaserData {
  id?: string;
  farmId: string;
  name: string | null;
  postCode: string | null;
  countryCode: COUNTRY_CODE | null;
}
export interface CropSaleData {
  id?: string;
  purchaserId?: string;
  purchaser: PurchaserData;
  soldYieldTs: number | null;
}

export interface FootprintingCropSalesPayload {
  footprintingFarmCropPrimaryDataId: string;
  farmId: string;
  cropSales: CropSaleData[];
}

export interface FootprintingDairyPrimaryData {
  id: string;
  farmId: string;
  year: number;
  isSubmitted?: boolean;
  isVerified?: boolean;
  milkProduction?: ENUM_FARM_FOOTPRINTING_DAIRY_MILK_PRODUCTION | null;
  milkYieldKg?: number | null;
  fatContentPct?: number | null;
  proteinContentPct?: number | null;
  feedAdditiveActive?: ENUM_FARM_FOOTPRINTING_DAIRY_FEED_ADDITIVES | null;
  grasslandFertiliserActive?: ENUM_DAIRY_GRASSLAND_FERTILISER_ACTIVE | null;
  cowsInPastureActive?: ENUM_FARM_FOOTPRINTING_DAIRY_COWS_IN_PASTURE | null;
  cowsInPasture?: number | null;
  daysInPasturePerYear?: number | null;
  avgHoursInPasturePerDay?: number | null;
  forageLossPct?: number | null;
  moorlandActive?: ENUM_FARM_FOOTPRINTING_DAIRY_MOORLAND | null;
  fodderProductionAreaHa?: number | null;
  annualWaterLevelCm?: number | null;
  amountStoredGasTankPct?: number | null;
  manureAdditiveActive?: ENUM_FARM_FOOTPRINTING_DAIRY_MANURE_ADDITIVE_ACTIVE | null;
  refrigerantsActive?: ENUM_DAIRY_REFRIGERANTS_ACTIVE | null;
  comment?: string | null;
}

export interface FootprintingDairyHerdDetails {
  id: string;
  calvesAmount?: number | null;
  avgCowLosses?: number | null;
  avgCowUsefulLifeMonths?: number | null;
  cowWeightKnown?: ENUM_FARM_FOOTPRINTING_DAIRY_KNOWN_COW_WEIGHT | null;
  averageCowWeightKg?: number | null;
  estimatedCowBodyCondition?: ENUM_FARM_FOOTPRINTING_DAIRY_COW_BODY_CONDITION | null;
  matFeedPct?: number | null;
  wholeMilkFeedPct?: number | null;
  ageOfFirstCalvingMonths?: number | null;
  timeBetweenCalvingDays?: number | null;
}

export interface FootprintingDairyHerdSize {
  id: string;
  primaryDataId: string;
  cowTypeId: string;
  cowCount: number;
  cowType?: DairyCowType;
}

export interface UploadableHerdSize {
  id?: string;
  cowTypeId: string;
  cowCount: number;
}

export interface UploadableFeedAdditive {
  id?: string;
  feedAdditiveId: string;
  amount: number | null;
  amountUnit: ENUM_DAIRY_FEED_ADDITIVE_UNITS | null;
}

export interface FootprintingDairyFeedAdditivePayload {
  footprintingFarmPrimaryDataId: string;
  selectedFeedAdditive: UploadableFeedAdditive[];
}

export type UploadableGrasslandFertiliser = {
  id?: string;
  fertiliserProductId: string;
  totalAmountApplied: number | null;
  totalAmountUnit: ENUM_FERTILIZER_UNITS | null;
};

export type FootprintingDairyGrasslandFertiliserPayload = {
  selectedGrasslandFertilisers: UploadableGrasslandFertiliser[];
};

export interface UploadableManureStorage {
  id?: string;
  manureStorageId: string;
  amountStoredPct: number | null;
}

export interface FootprintingDairyManureStoragePayload {
  footprintingFarmPrimaryDataId: string;
  selectedManureStorage: UploadableManureStorage[];
}

export interface UploadableManureAdditive {
  id?: string;
  manureAdditiveId: string;
  amountUsedM3: number | null;
  amountUsedM3Unit: ENUM_MANURE_ADDITIVE_UNITS | null;
  enrichedSlurryPct: number | null;
}

export interface FootprintingDairyManureAdditivePayload {
  footprintingFarmPrimaryDataId: string;
  selectedManureAdditive: UploadableManureAdditive[];
}

export interface FootprintingDairyCowSales {
  id: string;
  primaryDataId: string;
  farmId: string;
  cowSalesInPoorCondition: number | null;
  cowSalesForLightManufacturing: number | null;
  cowSalesForStandardManufacturing: number | null;
  cowSalesInGoodCondition: number | null;
  cowSalesInPremiumCondition: number | null;
}

export interface UploadableCowSales {
  id?: string;
  cowSalesInPoorCondition: number | null;
  cowSalesForLightManufacturing: number | null;
  cowSalesForStandardManufacturing: number | null;
  cowSalesInGoodCondition: number | null;
  cowSalesInPremiumCondition: number | null;
}

export interface UploadableFeedInformation {
  id?: string;
  feedTypeId: string;
  isSelfProduced: boolean | null;
  totalAmountFedTs?: number | null;
  silagePct: number | null;
  energyValue?: number | null;
  energyUnit?: ENUM_DAIRY_FEED_ENERGY_UNIT | null;
  dryMatterPct?: number | null;
  cowsPct?: number | null;
  calvesPct?: number | null;
  emissionFactorKgCo2Eq?: number | null;
}

export interface FootprintingDairyFeedInformation {
  id: string;
  primaryDataId: string;
  isSelfProduced: boolean | null;
  feedTypeId: string;
  feedType?: DairyFeedType;
  totalAmountFedTs: number | null;
  silagePct: number | null;
  energyValue: number | null;
  energyUnit: ENUM_DAIRY_FEED_ENERGY_UNIT | null;
  dryMatterPct: number | null;
  cowsPct: number | null;
  calvesPct: number | null;
  emissionFactorKgCo2Eq: number | null;
}

export interface DairyFeedProduction {
  id?: string;
  feedInformationId: string;
  primaryDataId?: string;
  cropUsedForFeed?: ENUM_FARM_FOOTPRINTING_DAIRY_FEED_PRODUCTION_CROP_USAGE | null;
  cropYieldTPerHa?: number | null;
  dieselConsumptionLHa?: number | null;
  energyUsedKwhHa?: number | null;
  mineralNitrogenKgHa?: number | null;
  mineralPhosphorusKgHa?: number | null;
  mineralPotassiumKgHa?: number | null;
  nitrogenOrganicFertKgHa?: number | null;
  totalLimeAppliedKgCaCo3PerHa?: number | null;
  totalPlantProtectionApplied?: number | null;
  totalPlantProtectionAppliedUnit?: ENUM_DAIRY_FEED_PRODUCTION_UNITS | null;
  totalWaterUsedIrregationLPerHa?: number | null;
  soldOffFarmPct?: number | null;
  fedToCowsPct?: number | null;
  otherUsesPct?: number | null;
}

export interface PorkFeedProduction {
  id?: string;
  feedInformationId: string;
  primaryDataId?: string;
  cropUsedForFeed?: ENUM_FARM_FOOTPRINTING_DAIRY_FEED_PRODUCTION_CROP_USAGE | null;
  cropYieldTPerHa?: number | null;
  dieselConsumptionLHa?: number | null;
  energyUsedKwhHa?: number | null;
  mineralNitrogenKgHa?: number | null;
  mineralPhosphorusKgHa?: number | null;
  mineralPotassiumKgHa?: number | null;
  nitrogenOrganicFertKgHa?: number | null;
  totalLimeAppliedKgCaCo3PerHa?: number | null;
  totalPlantProtectionApplied?: number | null;
  totalPlantProtectionAppliedUnit?: ENUM_DAIRY_FEED_PRODUCTION_UNITS | null;
  totalWaterUsedIrregationLPerHa?: number | null;
  soldOffFarmPct?: number | null;
  fedToPigsPct?: number | null;
  otherUsesPct?: number | null;
}

export interface PigletFeedProduction {
  id?: string;
  feedInformationId: string;
  primaryDataId?: string;
  cropUsedForFeed?: ENUM_FARM_FOOTPRINTING_DAIRY_FEED_PRODUCTION_CROP_USAGE | null;
  cropYieldTPerHa?: number | null;
  dieselConsumptionLHa?: number | null;
  energyUsedKwhHa?: number | null;
  mineralNitrogenKgHa?: number | null;
  mineralPhosphorusKgHa?: number | null;
  mineralPotassiumKgHa?: number | null;
  nitrogenOrganicFertKgHa?: number | null;
  totalLimeAppliedKgCaCo3PerHa?: number | null;
  totalPlantProtectionApplied?: number | null;
  totalPlantProtectionAppliedUnit?: ENUM_DAIRY_FEED_PRODUCTION_UNITS | null;
  totalWaterUsedIrregationLPerHa?: number | null;
  soldOffFarmPct?: number | null;
  fedToPigletsPct?: number | null;
  otherUsesPct?: number | null;
}

export interface BeefFeedProduction {
  id?: string;
  feedInformationId: string;
  primaryDataId?: string;
  cropUsedForFeed?: ENUM_FARM_FOOTPRINTING_DAIRY_FEED_PRODUCTION_CROP_USAGE | null;
  cropYieldTPerHa?: number | null;
  dieselConsumptionLHa?: number | null;
  energyUsedKwhHa?: number | null;
  mineralNitrogenKgHa?: number | null;
  mineralPhosphorusKgHa?: number | null;
  mineralPotassiumKgHa?: number | null;
  nitrogenOrganicFertKgHa?: number | null;
  totalLimeAppliedKgCaCo3PerHa?: number | null;
  totalPlantProtectionApplied?: number | null;
  totalPlantProtectionAppliedUnit?: ENUM_DAIRY_FEED_PRODUCTION_UNITS | null;
  totalWaterUsedIrregationLPerHa?: number | null;
  soldOffFarmPct?: number | null;
  fedToCowsPct?: number | null;
  otherUsesPct?: number | null;
}

/**
 * Waste and wastewater emissions PEAS has already attributed to a single commodity, so no area ratio
 * or allocation applies. Optional on every commodity result because older cached responses predate it.
 */
export interface CommodityWasteFootprint {
  wasteFootprint: FootprintingFootprintAggregation;
  wastewaterFootprint: FootprintingFootprintAggregation;
}

// New interfaces for the calculated footprint response structure
export interface CalculatedFarmFootprintResponse {
  farmId: string;
  year: number;
  calculatedFootprint: {
    energy: {
      dieselFootprint: FootprintingFootprintAggregation;
      benzinFootprint: FootprintingFootprintAggregation;
      naturalGasFootprint: FootprintingFootprintAggregation;
      heatingOilFootprint: FootprintingFootprintAggregation;
      electricityFootprint: FootprintingFootprintAggregation;
      renewableElectricityFootprint: FootprintingFootprintAggregation;
    };
    water: {
      /** @deprecated Use the commodity footprint's waste.* instead */
      wastewaterFootprint: FootprintingFootprintAggregation;
    };
    misc: {
      /** @deprecated Use the commodity footprint's waste.* instead */
      wasteFootprint: FootprintingFootprintAggregation;
      landUseChangeFooprint: Array<{
        id: string;
        footprints: {
          biomassEmissionFootprint: FootprintingFootprintAggregation;
          deadOrganicMatterFootprint: FootprintingFootprintAggregation;
          socChangeFootprint: FootprintingFootprintAggregation;
        };
      }>;
    };
  };
}

// Interfaces for crop footprint application arrays
export interface CropSeedingApplicationFootprint {
  seedingId: string;
  footprint: FootprintingFootprintAggregation;
}

export interface CropTillageApplicationFootprint {
  tillageId: string;
  footprint: FootprintingFootprintAggregation;
}

export interface CropFertilizerApplicationFootprint {
  fertilizerApplicationId: string;
  footprint: FootprintingFootprintAggregation;
}

export interface CropPlantProtectionApplicationFootprint {
  plantProtectionApplicationId: string;
  footprint: FootprintingFootprintAggregation;
}

export interface CropFertilizerFootprint {
  id: string;
  emissions: FootprintingFootprintAggregation;
}

export interface CropPlantProtectionFootprint {
  id: string;
  emissions: FootprintingFootprintAggregation;
}

export interface CropAgriculturalLimeFootprint {
  emissions: FootprintingFootprintAggregation;
}

export interface CropRetailerTransportEmissions {
  retailerId: string;
  footprint: FootprintingFootprintAggregation;
}

export interface CalculatedCropFootprintResponse {
  farmId: string;
  cropId: string;
  year: number;
  calculatedFootprint: {
    dieselFootprint: {
      harvestDieselUsage: {
        cropId: string;
        footprint: FootprintingFootprintAggregation;
      };
      tillageApplication: CropTillageApplicationFootprint[];
      seedingApplication: CropSeedingApplicationFootprint[];
      fertilizerApplication: CropFertilizerApplicationFootprint[];
      plantProtectionApplications: CropPlantProtectionApplicationFootprint[];
    };
    upstreamTransportationFootprint: {
      organicFertilizers: CropRetailerTransportEmissions[];
      mineralFertilizers: CropRetailerTransportEmissions[];
      plantProtection: CropRetailerTransportEmissions[];
      seeding: CropRetailerTransportEmissions[];
    };
    downstreamTransportationFootprint: {
      crop: Array<{
        purchaserId: string;
        footprint: FootprintingFootprintAggregation;
      }>;
    };
    mineralFertilizerFootprint: {
      directN2O: CropFertilizerFootprint[];
      directCO2Eq: CropFertilizerFootprint[];
      NH3VolatilizationN2O: CropFertilizerFootprint[];
      NH3VolatilizationCO2Eq: CropFertilizerFootprint[];
      leachedNitrateN2O: CropFertilizerFootprint[];
      leachedNitrateCO2Eq: CropFertilizerFootprint[];
      productionCO2Eq: CropFertilizerFootprint[];
      totalNeutralisation: CropFertilizerFootprint[];
    };
    organicFertilizerFootprint: {
      organicFertilizerDirectN2O: CropFertilizerFootprint[];
      organicFertilizerDirectCO2Eq: CropFertilizerFootprint[];
      leachedNitrateN2O: CropFertilizerFootprint[];
      leachedNitrateCO2Eq: CropFertilizerFootprint[];
      NH3VolatilizationN2O: CropFertilizerFootprint[];
      NH3VolatilizationCO2Eq: CropFertilizerFootprint[];
    };
    plantProtectionFootprint: CropPlantProtectionFootprint[];
    agriculturalLimeFootprint: CropAgriculturalLimeFootprint;
    irrigationFootprint: FootprintingFootprintAggregation;
    cropResidueFootprint: {
      directN2O: FootprintingFootprintAggregation;
      directN2OCO2Eq: FootprintingFootprintAggregation;
      leachedNitrateN20: FootprintingFootprintAggregation;
      leachedNitrateCO2Eq: FootprintingFootprintAggregation;
    };
    waste?: CommodityWasteFootprint;
  };
}

export interface FootprintingFootprintAggregation {
  amount: {
    soc: NullableRangeObject;
    emission: NullableRangeObject;
  };
  penalty: {
    soc: NullableRangeObject;
    emission: NullableRangeObject;
  };
}

export interface CropAssessment {
  id: string;
  fieldIds: string[];
  isSubmitted: boolean;
}

interface DairyDigestion {
  digestionCH4: FootprintingFootprintAggregation;
  digestionCO2eq: FootprintingFootprintAggregation;
}

interface DairyFeed {
  digestionCH4: FootprintingFootprintAggregation;
  digestionCO2eq: FootprintingFootprintAggregation;
  production: FootprintingFootprintAggregation;
}

interface DairyFeedLoss {
  feedLossIndirectN2O: FootprintingFootprintAggregation;
  feedLossIndirectCO2eq: FootprintingFootprintAggregation;
  feedLossDirectN2O: FootprintingFootprintAggregation;
  feedLossDirectCO2eq: FootprintingFootprintAggregation;
}

interface DairyManureManagement {
  storageCH4: FootprintingFootprintAggregation;
  storageCO2eq: FootprintingFootprintAggregation;
  storageIndirectN2O: FootprintingFootprintAggregation;
  storageIndirectCO2eq: FootprintingFootprintAggregation;
  storageDirectN2O: FootprintingFootprintAggregation;
  storageDirectCO2eq: FootprintingFootprintAggregation;
}

interface DairyPastureManagement {
  pastureCH4: FootprintingFootprintAggregation;
  pastureCO2eq: FootprintingFootprintAggregation;
  pastureIndirectN2O: FootprintingFootprintAggregation;
  pastureIndirectCO2eq: FootprintingFootprintAggregation;
  pastureDirectN2O: FootprintingFootprintAggregation;
  pastureDirectCO2eq: FootprintingFootprintAggregation;
}

export interface DairyFootprintCalculatedResult {
  stockRenewal: FootprintingFootprintAggregation;
  waterUsage: FootprintingFootprintAggregation;
  machinery: FootprintingFootprintAggregation;
  diesel: FootprintingFootprintAggregation;
  energy: FootprintingFootprintAggregation;
  calves: {
    feeding: DairyDigestion & DairyFeed;
    manureManagement: DairyManureManagement;
  };
  dairyCows: {
    bedding: FootprintingFootprintAggregation;
    feeding: DairyDigestion & DairyFeed & DairyFeedLoss;
    pasture: DairyPastureManagement;
    manureManagement: DairyManureManagement;
  };
  /** CFP only — categories with no direct LFL equivalent */
  misc?: {
    fertiliser?: FootprintingFootprintAggregation;
    refrigerants?: FootprintingFootprintAggregation;
  };
  waste?: CommodityWasteFootprint;
}

export interface CalculatedDairyFootprintResponse {
  calculatedFootprint: DairyFootprintCalculatedResult;
  farmId: string;
  year: number;
}

export interface BeefFootprintCalculatedResult {
  stockRenewal: FootprintingFootprintAggregation;
  waterUsage: FootprintingFootprintAggregation;
  machinery: FootprintingFootprintAggregation;
  diesel: FootprintingFootprintAggregation;
  energy: FootprintingFootprintAggregation;
  calves: {
    feeding: DairyDigestion & DairyFeed;
    manureManagement: DairyManureManagement;
  };
  beefCattle: {
    bedding: FootprintingFootprintAggregation;
    feeding: DairyDigestion & DairyFeed & DairyFeedLoss;
    pasture: DairyPastureManagement;
    manureManagement: DairyManureManagement;
  };
  misc?: {
    fertiliser?: FootprintingFootprintAggregation;
  };
  waste?: CommodityWasteFootprint;
}

export interface CalculatedBeefFootprintResponse {
  calculatedFootprint: BeefFootprintCalculatedResult;
  farmId: string;
  year: number;
}

/**
 * PORK MODELS
 */

export const PORK_FOOTPRINTING_MOORLAND_ACTIVE = {
  YES: "yes",
  NO: "no",
} as const;

export type ENUM_PORK_FOOTPRINTING_MOORLAND_ACTIVE = (typeof PORK_FOOTPRINTING_MOORLAND_ACTIVE)[keyof typeof PORK_FOOTPRINTING_MOORLAND_ACTIVE];

export const PIGLET_FOOTPRINTING_MOORLAND_ACTIVE = {
  YES: "yes",
  NO: "no",
} as const;
export type ENUM_PIGLET_FOOTPRINTING_MOORLAND_ACTIVE = (typeof PIGLET_FOOTPRINTING_MOORLAND_ACTIVE)[keyof typeof PIGLET_FOOTPRINTING_MOORLAND_ACTIVE];

export const PORK_FOOTPRINTING_MANURE_ADDITIVE_ACTIVE = {
  YES: "yes",
  NO: "no",
} as const;

export type ENUM_PORK_FOOTPRINTING_MANURE_ADDITIVE_ACTIVE =
  (typeof PORK_FOOTPRINTING_MANURE_ADDITIVE_ACTIVE)[keyof typeof PORK_FOOTPRINTING_MANURE_ADDITIVE_ACTIVE];

export const PIGLET_FOOTPRINTING_MANURE_ADDITIVE_ACTIVE = {
  YES: "yes",
  NO: "no",
} as const;

export type ENUM_PIGLET_FOOTPRINTING_MANURE_ADDITIVE_ACTIVE =
  (typeof PIGLET_FOOTPRINTING_MANURE_ADDITIVE_ACTIVE)[keyof typeof PIGLET_FOOTPRINTING_MANURE_ADDITIVE_ACTIVE];

export interface PorkFootprintCalculatedResult {
  feed?: {
    feeding: {
      feedCO2eq: FootprintingFootprintAggregation;
    };
  };
  husbandry?: {
    manureManagement: {
      manureCO2eq: FootprintingFootprintAggregation;
    };
    stockRenewal: {
      stockRenewalCO2eq: FootprintingFootprintAggregation;
    };
  };
  energy?: FootprintingFootprintAggregation;
  total?: {
    CO2eq: number;
    CO2eqPerKgLiveWeight: number;
    CO2eqPerKgCarcassWeight: number;
    CO2eqPerPlacePerYear: number;
    pigsProducedPerYear: number;
  };
  waste?: CommodityWasteFootprint;
}

export interface CalculatedPorkFootprintResponse {
  calculatedFootprint: PorkFootprintCalculatedResult;
  farmId: string;
  year: number;
}

export interface PigletFootprintCalculatedResult {
  feed?: {
    feeding: {
      feedCO2eq: FootprintingFootprintAggregation;
    };
  };
  husbandry?: {
    manureManagement: {
      manureCO2eq: FootprintingFootprintAggregation;
    };
    stockRenewal: {
      stockRenewalCO2eq: FootprintingFootprintAggregation;
    };
  };
  energy?: FootprintingFootprintAggregation;
  total?: {
    CO2eq: number;
    /** kg CO2eq per kg piglet sales weight (LFL: treibhausEmiJeKgLgFerkel) */
    CO2eqPerKgSalesWeight: number;
    /** kg CO2eq per sow per year (LFL: treibhausEmiJeSauUndJahr) */
    CO2eqPerSowPerYear: number;
  };
  waste?: CommodityWasteFootprint;
}

export interface CalculatedPigletFootprintResponse {
  calculatedFootprint: PigletFootprintCalculatedResult;
  farmId: string;
  year: number;
}

export interface FootprintingPorkPrimaryData {
  id: string;
  farmId: string;
  year: number;
  yieldKgs: number | null;
  liveWeight: number | null;
  manureAdditiveActive: ENUM_PORK_FOOTPRINTING_MANURE_ADDITIVE_ACTIVE | null;
  moorlandActive: ENUM_PORK_FOOTPRINTING_MOORLAND_ACTIVE | null;
  moorlandAreaForFodderProduction: number | null;
  moorlandAnnualWaterLevel: number | null;
  isSubmitted: boolean;
  isVerified?: boolean;
  comment: string | null;
}

export interface FootprintingPorkHerdDetails {
  id: string;
  primaryDataId: string;
  averageHerdSize: number | null;
  rearingPeriodDays: number | null;
  pigLossPct: number | null;
}

export type UploadablePorkFeedInformation = {
  id?: string;
  feedId?: string | null;
  isSelfProduced?: boolean | null;
  totalAmountFedTs?: number | null;
  silagePct?: number | null;
  energyValue?: number | null;
  dryMatterPct?: number | null;
  emissionFactorKgCo2Eq?: number | null;
};

export interface FootprintingPorkFeedInformation {
  id: string;
  primaryDataId: string;
  isSelfProduced: boolean | null;
  feedId: string;
  feedType?: PorkFeedType;
  totalAmountFedTs: number | null;
  silagePct: number | null;
  energyValue: number | null;
  dryMatterPct: number | null;
  emissionFactorKgCo2Eq: number | null;
}

export interface FootprintingPorkManureStoragePayload {
  primaryDataId: string;
  selectedManureStorage: UploadableManureStorage[];
}

export interface FootprintingPorkManureAdditivePayload {
  primaryDataId: string;
  selectedManureAdditive: UploadableManureAdditive[];
}

export interface FootprintingBeefManureAdditivePayload {
  primaryDataId: string;
  selectedManureAdditive: UploadableManureAdditive[];
}

export type FootprintingPorkPigletSourcingPayload = {
  sourcingType: ENUM_PORK_PIGLET_SOURCING_TYPE;
  sourceFarms: Array<{
    id?: string;
    sourceFarmName: string | null;
    vvvoIdentifier: string | null;
    quantityPurchased: number | null;
    averageWeightKg: number | null;
  }>;
};

export type FootprintingPorkStablePayload = {
  stableType: ENUM_PORK_STABLE_TYPE | null;
  stableSize: number | null;
  hasAmmoniaFiltration: boolean | null;
  ammoniaFiltrationEfficiency: ENUM_PORK_AMMONIA_FILTRATION_EFFICIENCY | null;
};

export type FootprintingPorkStablesPayload = {
  stables: FootprintingPorkStablePayload[];
};

export interface FootprintingPigletPrimaryData {
  id: string;
  farmId: string;
  year: number;
  vvvoIdentifier: string | null;
  averageSalesWeightKg: number | null;
  averageNumberOfLitters: number | null;
  averageLitterSize: number | null;
  manureAdditiveActive: ENUM_PIGLET_FOOTPRINTING_MANURE_ADDITIVE_ACTIVE | null;
  moorlandActive: ENUM_PIGLET_FOOTPRINTING_MOORLAND_ACTIVE | null;
  moorlandAreaForFodderProduction: number | null;
  moorlandAnnualWaterLevel: number | null;
  isSubmitted: boolean;
  isVerified?: boolean;
  comment: string | null;
}

export interface FootprintingPigletHerdDetails {
  id: string;
  primaryDataId: string;
  averageNumberOfSows: number | null;
  yearlyReplacementRatePct: number | null;
  sucklingPigletLossRatePct: number | null;
  rearingLossPct: number | null;
  soldSucklingPigsPct: number | null;
}

export type UploadablePigletFeedInformation = {
  id?: string;
  feedId?: string | null;
  isSelfProduced?: boolean | null;
  totalAmountFedTs?: number | null;
  silagePct?: number | null;
  energyValue?: number | null;
  dryMatterPct?: number | null;
  pigletsPct?: number | null;
  sowsPct?: number | null;
  emissionFactorKgCo2Eq?: number | null;
};

export interface FootprintingPigletFeedInformation {
  id: string;
  primaryDataId: string;
  isSelfProduced: boolean | null;
  feedId: string;
  feedType?: PigletFeedType;
  totalAmountFedTs: number | null;
  silagePct: number | null;
  energyValue: number | null;
  dryMatterPct: number | null;
  pigletsPct: number | null;
  sowsPct: number | null;
  emissionFactorKgCo2Eq: number | null;
}

export interface FootprintingPigletManureStoragePayload {
  primaryDataId: string;
  selectedManureStorage: UploadableManureStorage[];
}

export interface FootprintingPigletManureAdditivePayload {
  primaryDataId: string;
  selectedManureAdditive: UploadableManureAdditive[];
}

export type FootprintingPigletStablePayload = {
  stableType: ENUM_PIGLET_STABLE_TYPE | null;
  stableSize: number | null;
  hasAmmoniaFiltration: boolean | null;
  ammoniaFiltrationEfficiency: ENUM_PIGLET_AMMONIA_FILTRATION_EFFICIENCY | null;
};

export type FootprintingPigletStablesPayload = {
  stables: FootprintingPigletStablePayload[];
};
